<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="onMenuMouseLeave"
  >
    <!-- main menu header-->
    <div
      class="navbar-header expanded header officeToggler"
      :class="isRoyalTenant ? 'pb-5' : ''"
    >
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul
          class="nav navbar-nav flex-row"
          :class="{ 'nav-header': !isVerticalMenuCollapsed || isMouseHovered }"
        >

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="25"
                class="d-block d-xl-none toggle-icon"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="isRoyalTenant ? collapseRoyalTogglerIconFeather : collapseTogglerIconFeather"
                size="25"
                class="d-none d-xl-block collapse-toggle-icon toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
        <div
          class="icon-row justify-content-center align-items-center my-2"
          :style="{ display: !isVerticalMenuCollapsed || isMouseHovered ? 'flex' : 'none'}"
        >
          <cal-dropdown />
          <div
            id="waybills"
            class="bg-circle"
            @click="handleCickForWaybill"
          >
            <feather-icon
              icon="FileMinusIcon"
              size="21"
              class="file-icon"
            />
          </div>
          <div
            id="tracking"
            class="icons-circle"
          >
            <a
              :href="getTrackingLink()"
              target="_blank"
            >
              <feather-icon
                icon="MapPinIcon"
                size="21"
                class="tracking-icon"
              />
            </a>
          </div>
        </div>
        <ul
          class="nav navbar-nav flex-row mt-1"
          :style="{ justifyContent: !isVerticalMenuCollapsed || isMouseHovered ? 'center' : 'left', height: !isVerticalMenuCollapsed || isMouseHovered ? '60px' : '52px' }"
        >
          <li class="nav-item justify-content-center d-flex">
            <div
              v-if="loading"
              class="spinner-border"
              role="status"
            >
              <span class="sr-only">Loading...</span>
            </div>
            <div
              v-else
              :class="isRoyalTenant ? '' : 'd-flex align-items-center logo-bg'"
            >
              <b-img
                v-if="!loading && isRoyalTenant"
                :src="courier.company_logo ? `${bucketUrl}${courier.company_logo}` : defaultCompanyLogo"
                alt="logo"
                :style="{ width: !isVerticalMenuCollapsed || isMouseHovered ? '141px' : '53px'}"
              />
              <b-img
                v-if="!loading && !isRoyalTenant"
                :src="courier.company_logo ? `${bucketUrl}${courier.company_logo}` : defaultCompanyLogo"
                alt="logo"
                :style="{ width: !isVerticalMenuCollapsed || isMouseHovered ? '50px' : '22px', borderRadius: '7px'}"
              />
              <span
                v-if="!isRoyalTenant"
                class="company-name mx-2"
              >{{ form.company_name }}</span>
            </div>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import {
  bucketUrl, defaultCompanyLogo, tenant, appEnv,
} from '@/constants/config'
import { $themeConfig } from '@themeConfig'
import ErrorMessage from '@core/mixins/ErrorMessage'
import navMenuItems from '@/navigation/vertical'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import CalDropdown from '@core/layouts/components/app-navbar/components/CalDropdown.vue'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

const BusinessRepository = RepositoryFactory.get('businessSetting')

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    CalDropdown,
  },
  mixins: [ErrorMessage],
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      form: {},
      appEnv,
      tenant,
      isRateCalculatorOpen: false,
    }
  },
  computed: {
    businessSettings() {
      return this.courier || JSON.parse(localStorage.getItem('businessSettings'))
    },
    isRoyalTenant() {
      return this.tenant === 'royalexpress'
    },
  },
  mounted() {
    this.fetchBusinessSettings()
    this.form = JSON.parse(localStorage.getItem('businessSettings'))
    this.$root.$on('rate-calculator-open', this.setRateCalculatorOpen)
  },
  beforeDestroy() {
    this.$root.$off('rate-calculator-open', this.setRateCalculatorOpen)
  },
  methods: {
    setRateCalculatorOpen(isOpen) {
      this.isRateCalculatorOpen = isOpen
      if (isOpen) {
        this.updateMouseHovered(true)
      }
    },
    onMenuMouseLeave() {
      if (!this.isRateCalculatorOpen) {
        this.updateMouseHovered(false)
      }
    },
    async fetchBusinessSettings() {
      try {
        this.loading = true
        this.courier = localStorage.getItem('businessSettings') ? JSON.parse(localStorage.getItem('businessSettings')) : (await BusinessRepository.getbusinessSettingsResource()).data.data
        if (!localStorage.getItem('businessSettings')) localStorage.setItem('businessSettings', JSON.stringify(this.courier))
      } catch (e) {
        // this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    handleCickForWaybill() {
      this.$router.push('/waybill-request')
    },
    emitRateCalculatorClick() {
      this.$root.$emit('rate-calculator-clicked')
    },
    openCalculator() {
      // Emit an event to open the calculator
      this.$root.$emit('open-calculator')
    },
    getTrackingLink() {
      if (this.appEnv === 'local') {
        return 'http://localhost:3000'
      }
      if (this.appEnv === 'staging' || window.location.origin.split('.')[1] === 'merchant') {
        return window.location.origin.replaceAll('merchant', 'tracking')
      }
      // if url inlude curfox return below one
      if (window.location.host.includes('curfox')) {
        return `https://${tenant}.tracking.curfox.com`
      // eslint-disable-next-line no-else-return
      } else {
        // else replace office/merchant with tracking
        return window.location.origin.replaceAll('merchant', 'tracking')
      }
    },
  },

  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))
    const collapseRoyalTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'AlignRightIcon' : 'AlignJustifyIcon'))

    // App Name
    const { appName } = $themeConfig.app

    return {
      loading: false,
      courier: {},
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,
      collapseRoyalTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      bucketUrl,
      defaultCompanyLogo,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
.header {
  margin-bottom: 100px;
}
.officeToggler {
  margin-top: 4px;
}
.brand-text{
  line-height: 1;
}
.toggle-icon{
  width: 23px;
  height: 23px;
}
.icon-row {
  .file-icon {
    transition: transform 0.3s;
  }

  .file-icon:hover {
    transform: scale(1.1);
  }
}
.logo-container {
  width: 100px; /* Adjust as necessary */
  height: auto;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.logo-img {
  max-width: 100%;
  max-height: 100%;
}
.icons-circle, .bg-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: var(--primary);
  cursor: pointer;
  position: relative;
  margin-left: 5px;
  margin-right: 5px;
}
.logo-bg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: fit-content;
  border-radius: 5px;
  background-color: #F9F9F9;
  cursor: pointer;
  position: relative;
  margin-left: 5px;
  margin-right: 5px;
}
.tracking-icon, .file-icon{
  color: white;
}
</style>
